import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { SEOHead } from '@/components/atoms/SEOHead';
import { JsonLd } from '@/components/atoms/JsonLd';

const FAQ = [
  {
    category: '계정',
    items: [
      { q: '회원가입은 어떻게 하나요?', a: '카카오/네이버/구글 소셜 로그인으로 3초 만에 시작할 수 있어요.' },
      { q: '닉네임 변경은 언제 가능한가요?', a: '30일에 1회 변경할 수 있어요. 마이페이지 > 프로필 수정에서 변경 가능합니다.' },
      { q: '탈퇴하면 포인트는 어떻게 되나요?', a: '탈퇴 시 보유 포인트는 모두 소멸되며 복구되지 않아요. 탈퇴 전 교환을 권장해요.' },
    ],
  },
  {
    category: '포인트',
    items: [
      { q: '포인트 가치는?', a: '1P = 1원이에요. 적립한 포인트를 그대로 기프티콘 등으로 교환할 수 있어요.' },
      { q: '포인트는 어떻게 적립되나요?', a: '가입 보너스 200P, 출석체크 10P, 게시글 20P, 댓글 5P, 매매일지 7P+, 친구 추천 100P(추천인)/50P(피추천인) 등으로 적립돼요.' },
      { q: '하루 적립 한도가 있나요?', a: '하루 최대 200P까지 적립 가능해요 (악용 방지). 가입 보너스와 친구 추천은 상한과 무관해요.' },
      { q: '포인트 교환 최소 보유는?', a: '잔액 5,000P 이상일 때 교환을 신청할 수 있어요.' },
      { q: '포인트도 만료되나요?', a: '네, 적립일로부터 1년 후 자동 소멸돼요. 소멸 30일 전에 이메일로 안내드려요.' },
    ],
  },
  {
    category: '프리미엄',
    items: [
      { q: '프리미엄은 어떤 혜택이 있나요?', a: '프리미엄 게시판(매매일지/AI투자/거시경제/절세) 열람, 포인트 1.5배, 광고 제거, 급등 알림 우선순위 등이 있어요.' },
      { q: '가격은 얼마인가요?', a: '월 2,900원으로 스타벅스 한 잔 가격이에요. 토스페이먼츠로 결제됩니다.' },
      { q: '환불 정책은?', a: '7일 이내 사용 이력이 없으면 전액 환불 가능해요. 문의하기를 통해 신청해주세요.' },
    ],
  },
  {
    category: '투자',
    items: [
      { q: '시세는 얼마나 실시간인가요?', a: '코인은 업비트 WebSocket으로 초 단위, 주식은 공식 API로 분 단위 갱신돼요.' },
      { q: '급등 감지는 어떻게 작동하나요?', a: '1분마다 전 종목을 스캔해 ±5% 이상 변동 시 자동 토론 게시글이 생성돼요. 같은 종목은 60분 쿨타임이 있어요.' },
      { q: '모의투자 수수료는?', a: '0.05% (실거래 평균 수준)를 자동 차감해요. 초기 자본 1,000만원으로 시작합니다.' },
    ],
  },
];

export function FaqPage() {
  const [open, setOpen] = useState<string | null>(null);

  // FAQPage JSON-LD — 모든 카테고리 질문/답변 평탄화
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ.flatMap((section) =>
      section.items.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    ),
  };

  return (
    <div className="container-base py-10">
      <SEOHead
        title="자주 묻는 질문"
        description="시세공간 이용 중 자주 묻는 질문과 답변을 확인하세요."
        canonical="/faq"
      />
      <JsonLd data={faqSchema} />
      <h1 className="mb-2 text-2xl font-extrabold">자주 묻는 질문</h1>
      <p className="mb-6 text-sm text-[var(--color-text-sub)]">
        원하는 답변이 없다면 문의하기를 이용해주세요.
      </p>

      {FAQ.map((section) => (
        <section key={section.category} className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-[var(--color-primary)]">
            {section.category}
          </h2>
          <div className="space-y-2">
            {section.items.map((item, i) => {
              const key = `${section.category}-${i}`;
              const isOpen = open === key;
              return (
                <div
                  key={key}
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]"
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : key)}
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <span className="pr-4 text-sm font-medium">{item.q}</span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 flex-shrink-0 transition-transform',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>
                  {isOpen && (
                    <div className="border-t border-[var(--color-border)] p-4 text-sm text-[var(--color-text-sub)]">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
